import { Modal, Box, Typography, Button, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { createLockerArea, updateLockerArea } from '../../../../../api/ticket';
import { useSelector } from 'react-redux';

// Styled Components
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '420px',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
  borderRadius: '16px',
  overflow: 'hidden',
});

const ModalHeader = styled(Box)({
  height: '56px',
  backgroundColor: '#262626',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
});

const HeaderTitle = styled(Typography)({
  color: '#fff',
  fontSize: '18px',
  fontWeight: 600,
});

const CloseButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: '#fff',
    width: '24px',
    height: '24px',
  },
});

const ModalContent = styled(Box)({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const FormLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  minWidth: '80px',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#E0E0E0',
    },
    '&:hover fieldset': {
      borderColor: '#BDBDBD',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1F3A5F',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    '&::placeholder': {
      color: '#999',
      opacity: 1,
    },
  },
});

const ModalFooter = styled(Box)({
  padding: '16px 24px 24px 24px',
  display: 'flex',
  gap: '12px',
});

const CancelButton = styled(Button)({
  flex: 1,
  height: '48px',
  borderRadius: '8px',
  border: '1px solid #E0E0E0',
  backgroundColor: '#fff',
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

const SubmitButton = styled(Button)({
  flex: 2,
  height: '48px',
  borderRadius: '8px',
  backgroundColor: '#1F3A5F',
  fontSize: '16px',
  fontWeight: 500,
  color: '#fff',
  '&:hover': {
    backgroundColor: '#172C48',
  },
});

// Main Component
const LockerSpotEditModal = ({ branchIdData, open, handleClose, editData, onSuccess, isEditMode = false }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const [formData, setFormData] = useState({
    lockerAreaName: '',
    lockerAreaCount: '',
    startNumber: '',
  });

  useEffect(() => {
    if (editData && isEditMode) {
      setFormData({
        lockerAreaName: editData.lockerAreaName || '',
        lockerAreaCount: editData.lockerAreaCount || '',
        startNumber: editData.startNumber || '',
      });
    } else {
      setFormData({
        lockerAreaName: '',
        lockerAreaCount: '',
        startNumber: '',
      });
    }
  }, [editData, isEditMode, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (isEditMode) {
      try {
        await updateLockerArea(branchIdData, editData?.id, user?.token, formData);
        handleClose();
        onSuccess?.();
      } catch (error) {
        console.log('에러');
      }
    } else {
      try {
        await createLockerArea(branchIdData, user?.token, formData);
        handleClose();
        onSuccess?.();
      } catch (error) {
        console.log('handleSubmit 함수에러 : ' + error);
      }
    }
  };
  return (
    <Modal open={open} onClose={handleClose} disableEscapeKeyDown>
      <ModalContainer>
        {/* Header */}
        <ModalHeader>
          <HeaderTitle>{isEditMode ? t('라커 구역 수정') : t('라커 구역 추가')}</HeaderTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        {/* Content */}
        <ModalContent>
          {/* 락커 구역명 */}
          <FormRow>
            <FormLabel>{t('라커 구역명')}</FormLabel>
            <StyledTextField
              fullWidth
              size="small"
              placeholder={t('구역명을 입력해주세요')}
              value={formData.lockerAreaName}
              onChange={e => handleChange('lockerAreaName', e.target.value)}
            />
          </FormRow>

          {/* 락커 수량 */}
          <FormRow>
            <FormLabel>{t('라커 수량')}</FormLabel>
            <StyledTextField
              fullWidth
              size="small"
              placeholder={t('수량을 입력해주세요')}
              value={formData.lockerAreaCount}
              onChange={e => handleChange('lockerAreaCount', e.target.value)}
              type="number"
            />
          </FormRow>

          {/* 락커 시작번호 */}
          <FormRow>
            <FormLabel>{t('라커 시작번호')}</FormLabel>
            <StyledTextField
              fullWidth
              size="small"
              placeholder={t('시작 번호를 입력해주세요')}
              value={formData.startNumber}
              onChange={e => handleChange('startNumber', e.target.value)}
              type="number"
            />
          </FormRow>
        </ModalContent>

        {/* Footer */}
        <ModalFooter>
          <CancelButton onClick={handleClose}>{t('취소')}</CancelButton>
          <SubmitButton onClick={() => handleSubmit()}>{isEditMode ? t('수정') : t('추가')}</SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export default LockerSpotEditModal;
